/**
 * Phase 6 cross-cutting PR 5 — `<TopRailUnread>`.
 *
 * Ultra (≥ 1920 px) chrome element: surfaces unread notification
 * count as a chip at the top of the right rail. Complements the
 * SideNav-anchored bell badge — at Ultra, with 1920px of width to
 * spend, repeating the unread signal in the rail keeps it visible
 * without forcing the user to look down-left at the bell.
 *
 * Sourced from:
 *   - HANDOFF.md PR 5 acceptance: "Ultra (≥ 1920) surfaces unread
 *     count as a top-rail chip ('3 new · open ›')"
 *   - direction.md §1 PRESENTATION row: "Ultra surfaces unread count
 *     in the top-rail; dropdown stays bell-anchored"
 *
 * Wire-up:
 *   - Reads `useNotifications().unreadCount` directly (same 60s poll
 *     the bell uses; React memoization avoids duplicate requests).
 *   - On click, OPENS the notifications dropdown (W1 fix). The chip's
 *     label is "open ›" — previously it only `.focus()`ed the bell
 *     without opening the panel, so the advertised action did nothing
 *     (audit W1). It now triggers the bell's own click handler, so the
 *     bell remains the single source of dropdown state (one open path,
 *     no competing state). If the dropdown is already open we leave it
 *     alone (just bring it into view) rather than toggling it shut.
 *
 * Self-gates on:
 *   - viewport ≥ 1920 via CSS @media (component mounts, paints only
 *     at Ultra; matches StreakChip pattern)
 *   - `unreadCount > 0` (zero-state hides the chip entirely — chrome
 *     should disappear when there's nothing to surface, not show "0
 *     new")
 *
 * Copy: "3 new · open ›" matches the HANDOFF.md spec verbatim. "9+"
 * cap matches the bell badge convention (locked from vanilla).
 */

import { useCallback } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import styles from './TopRailUnread.module.css';

export function TopRailUnread() {
  const { unreadCount } = useNotifications();

  const onClick = useCallback(() => {
    // Find the BellTrigger in the SideNav footer and OPEN its dropdown
    // by triggering the bell's own click handler (the bell owns the
    // `isOpen` state — driving it through .click() keeps a single open
    // path instead of competing state). `aria-expanded` guards against
    // toggling an already-open panel shut.
    //
    // Selector uses prefix-match because BellTrigger's aria-label is
    // dynamic: "Notifications" when zero unread, "Notifications · N
    // unread" otherwise. TopRailUnread renders only when N > 0, so
    // the bell label always carries the "Notifications" prefix when
    // this handler runs.
    const bell = document.querySelector<HTMLElement>('[aria-label^="Notifications"]');
    if (!bell) return;
    if (bell.getAttribute('aria-expanded') !== 'true') {
      bell.click();
    }
    bell.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  if (unreadCount <= 0) return null;

  const display = unreadCount > 9 ? '9+' : String(unreadCount);
  const labelNoun = unreadCount === 1 ? 'new notification' : 'new notifications';

  return (
    <button
      type="button"
      className={styles.chip}
      onClick={onClick}
      aria-label={`${unreadCount} ${labelNoun}, jump to notifications bell`}
    >
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.count}>{display} new</span>
      <span className={styles.sep} aria-hidden="true">·</span>
      <span className={styles.cta}>open ›</span>
    </button>
  );
}
